import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { Box, capitalize, IconButton, Paper, Typography } from "@mui/material";
import useAPI from "../api/hooks/useAPI";
import useColors from "../api/hooks/useColors";
import SvgToIcon from "../icons/SvgToIcon";
import { icons } from "../scenes/stack/environment/icons/iconExport";
import { extractXmlContent } from "../utils/xmlFormatter";
import CustomToolTip from "./CustomTooltip";
import { Toast } from "./Toast";

const Evidence = ({ fileNameToDownload = "evidence.txt", evidence }) => {
  const colors = useColors();
  const { trackEvent } = useAPI();

  const getConfigFormat = (evidenceData) => {
    if (typeof evidenceData === "object" && evidenceData?.config_format) {
      return evidenceData.config_format;
    }
    return "json";
  };

  const getBadConfigFormat = (evidenceData) => {
    if (typeof evidenceData === "object" && evidenceData?.bad_config_format) {
      return evidenceData.bad_config_format;
    }
    return "json";
  };

  const handleCopy = (codeBlock, codeBlockName) => {
    trackEvent(`User copied ${codeBlockName} information`).then();
    const format = getConfigFormat(codeBlock);
    const content =
      format === "xml"
        ? extractXmlContent(codeBlock)
        : JSON.stringify(codeBlock, null, 2);
    navigator.clipboard.writeText(content);
    Toast.success(
      `${capitalize(codeBlockName)} information copied to clipboard!`
    );
  };

  const isJsonString = (str) => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  };

  const formatConfigData = (config) => {
    const configObj = typeof config === "string" ? JSON.parse(config) : config;

    // Helper function to parse nested JSON strings
    const parseNestedJson = (obj) => {
      // Return arrays directly as they do not require further parsing.
      if (Array.isArray(obj)) return obj;
      const newObj = { ...obj };
      for (const key in newObj) {
        if (typeof newObj[key] === "string" && isJsonString(newObj[key])) {
          try {
            newObj[key] = JSON.parse(newObj[key]);
          } catch (e) {
            // Keep original value if parsing fails
          }
        }
      }
      return newObj;
    };

    return JSON.stringify(parseNestedJson(configObj), null, 2);
  };

  const handleDownload = (codeBlock, fileName) => {
    trackEvent(`User downloaded ${fileName}`).then();

    const format = getConfigFormat(codeBlock);

    let content;
    if (format === "xml") {
      content = extractXmlContent(codeBlock);
    } else {
      if (typeof codeBlock === "string") {
        try {
          const parsed = JSON.parse(codeBlock);
          content = JSON.stringify(parsed, null, 2);
        } catch (e) {
          content = codeBlock;
        }
      } else {
        content = JSON.stringify(codeBlock, null, 2);
      }
    }

    const mimeType = format === "xml" ? "application/xml" : "application/json";

    const baseFileName = fileName.replace(/\.(txt|json|xml)$/i, "");
    const extension = format === "xml" ? ".xml" : ".json";
    const finalFileName = `${baseFileName}${extension}`;

    // Create a Blob with appropriate MIME type
    const blob = new Blob([content], { type: mimeType });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", finalFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatEvidenceContent = (evidenceData) => {
    // Check if this is evidence with bad_config_format (for misconfigured evidence)
    const badFormat = getBadConfigFormat(evidenceData);
    const format = getConfigFormat(evidenceData);

    // Use bad_config_format for the evidence content if it exists
    const contentFormat = badFormat !== "json" ? badFormat : format;

    if (contentFormat === "xml") {
      return extractXmlContent(evidenceData);
    } else {
      return typeof evidenceData === "object" || isJsonString(evidenceData)
        ? formatConfigData(evidenceData)
        : evidenceData;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <SvgToIcon
            IconLogo={icons.evidenceIcon}
            width={"24px"}
            style={{ marginRight: "8px", color: colors.common_white }}
          />
          <Typography variant="subtitle1">Evidence</Typography>
        </Box>
        <Box display={"flex"} gap={1}>
          <Box
            sx={{
              border: "1px solid " + colors.divider2,
              borderRadius: "8px",
              padding: "4px",
              width: "28px",
              height: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CustomToolTip title="Copy the evidence">
              <IconButton
                aria-label="copy"
                onClick={() => handleCopy(evidence, "evidence")}
                size="small"
              >
                <ContentCopyRoundedIcon
                  sx={{ color: colors.common_black, fontSize: "16px" }}
                />
              </IconButton>
            </CustomToolTip>
          </Box>
          <Box
            sx={{
              border: "1px solid " + colors.divider2,
              borderRadius: "8px",
              padding: "4px",
              width: "28px",
              height: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CustomToolTip title="Download the evidence">
              <IconButton
                aria-label="download"
                onClick={() =>
                  handleDownload(
                    evidence,
                    fileNameToDownload
                      ? fileNameToDownload
                      : "fileNameToDownload"
                  )
                }
                sx={{ color: colors.common_black }}
                size="small"
              >
                <DownloadRoundedIcon
                  sx={{ color: colors.common_black, fontSize: "16px" }}
                />
              </IconButton>
            </CustomToolTip>
          </Box>
        </Box>
      </Box>

      <Paper
        style={{
          padding: "16px",
          backgroundColor: colors.grey_8percent,
          whiteSpace: "pre-wrap",
          fontFamily: "Menlo",
          fontWeight: "400",
          borderRadius: "6px",
          fontSize: "12px",
          boxShadow: "none",
          border: `1px solid ${colors.divider2}`,
          height: "100%",
          overflow: "auto",
          position: "relative",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        {formatEvidenceContent(evidence)}
      </Paper>
    </Box>
  );
};

export default Evidence;
